<template>
  <div>
    <!-- Label -->
    <label :class="labelComputedClasses" :for="inputId">
      <span>{{ label }}</span>
      <ips-tooltip v-if="tooltip" :tooltip="tooltip">
        <template slot="tip">
          <ips-icon icon="ico-help"></ips-icon>
        </template>
      </ips-tooltip>
    </label>

    <div :class="formComputedClasses">
      <vue-tel-input
        v-model="phoneNumber"
        :value="phoneNumber"
        v-bind="$props"
        class="form-control"
        :class="{ invalid: showInvalidBorder }"
        :all-countries="allCountries"
        :auto-format="autoFormat"
        :custom-validate="customValidate"
        :default-country="defaultCountry"
        :disabled="disabled"
        :auto-default-country="autoDefaultCountry"
        :dropdown-options="dropdownOptions"
        :ignored-countries="ignoredCountries"
        :input-options="inputOptions"
        :invalid-msg="invalidMsg"
        :mode="mode"
        :only-countries="onlyCountries"
        :preferred-countries="preferredCountries"
        :style-classes="styleClasses"
        :valid-characters-only="validCharactersOnly"
        @input="input"
        @validate="validate"
        @country-changed="countryChanged"
        @blur="blur"
        @focus="focus"
        @space="space"
        @enter="enter"
        @open="open"
        @close="close"
      >
        <span v-if="!showArrowIcon" slot="arrow-icon"></span>
        <span slot="icon-right"></span>
      </vue-tel-input>

      <!-- Icon Append -->
      <ips-icon
        v-if="iconAppend"
        :icon="iconAppend"
        class="ips-form-icon--append"
        @click="appendIconClicked"
      ></ips-icon>

      <!-- Clear Icon -->
      <ips-icon
        v-show="phoneNumber && clearable"
        icon="ico-delete"
        class="ips-form-icon--clear"
        @click="clearInputValue"
      ></ips-icon>

      <!-- error validation -->
      <small v-if="error" class="ips-validation form-text text-danger">
        <ips-icon icon="ico-danger" size="sm"></ips-icon>
        <slot name="errorText"></slot>
      </small>

      <!-- warning validation -->
      <small v-if="warning" class="ips-validation form-text text-warning">
        <ips-icon icon="ico-warning" size="sm"></ips-icon>
        <slot name="errorText"></slot>
      </small>
    </div>
  </div>
</template>

<script>
import { VueTelInput } from 'vue-tel-input';
import libphonenumber from 'google-libphonenumber';
import IpsIcon from '@ipscape/ips-icon';
import IpsTooltip from '@ipscape/ips-tooltip';
import { formFieldsMixin } from '../../../mixins/index.js';

export default {
  name: 'IpsTelInput',
  components: { IpsIcon, IpsTooltip, VueTelInput },
  mixins: [formFieldsMixin],
  props: {
    inputId: {
      name: 'input id',
      description: 'The id of the input',
      type: String,
      default: '',
      required: false,
    },
    showArrowIcon: {
      name: 'showArrowIcon',
      type: Boolean,
      default: false,
      description: 'Show arrow icon next to flag',
      required: false,
    },
    showDialCodeInList: {
      name: 'showDialCodeInList',
      type: Boolean,
      default: true,
      description: 'showDialCodeInList',
      required: false,
    },
    showDialCodeInSelection: {
      name: 'showDialCodeInSelection',
      type: Boolean,
      default: false,
      description: 'showDialCodeInSelection',
      required: false,
    },
    showFlags: {
      name: 'showFlags',
      type: Boolean,
      default: true,
      description: 'showFlags',
      required: false,
    },
    maxlength: {
      name: 'maxlength',
      type: Number,
      default: 25,
      description: 'maxlength',
      required: false,
    },
    autofocus: {
      name: 'autofocus',
      type: Boolean,
      default: false,
      description: 'autofocus',
      required: false,
    },
    name: {
      name: 'name',
      description: 'name',
      type: String,
      default: 'telephone',
      required: false,
    },
    inputStyleClasses: {
      type: [String, Array, Object],
      default: '',
    },
    warning: {
      type: Boolean,
      default: false,
    },
    validationType: {
      type: String,
      default: 'valid', // 'valid' or 'possible'
    },
    allowEmergencyNumber: {
      type: Boolean,
      default: false,
    },
    // vue-tel-input props
    allCountries: {
      type: Array,
    },
    autoFormat: {
      type: Boolean,
      default: true,
    },
    customValidate: {
      type: [Boolean, RegExp],
      default: false,
    },
    defaultCountry: {
      // Default country code, ie: 'AU'
      // Will override the current country of user
      type: [String, Number],
      default: '',
    },
    autoDefaultCountry: {
      type: Boolean,
      default: true,
    },
    ignoredCountries: {
      type: Array,
      default: () => {
        return [];
      },
    },
    invalidMsg: {
      type: String,
      default: '',
    },
    mode: {
      type: String,
      default: 'international',
    },
    onlyCountries: {
      type: Array,
      default: () => {
        return [];
      },
    },
    preferredCountries: {
      type: Array,
      default: () => {
        return [];
      },
    },
    styleClasses: {
      type: [String, Array, Object],
      default: '',
    },
    validCharactersOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      phoneNumber: this.value,
      phoneNumberInfo: null,
    };
  },
  computed: {
    isValidPhoneNumber() {
      return this.phoneNumberInfo && this.phoneNumberInfo.valid;
    },
    showInvalidBorder() {
      return !this.isValidPhoneNumber && this.phoneNumber !== '';
    },
    dropdownOptions() {
      return {
        showDialCodeInList: this.showDialCodeInList,
        showDialCodeInSelection: this.showDialCodeInSelection,
        showFlags: this.showFlags,
        tabindex: this.tabindex,
      };
    },
    inputOptions() {
      return {
        autocomplete: this.autocomplete,
        autofocus: this.autofocus,
        id: this.inputId,
        maxlength: this.maxlength,
        name: this.name,
        placeholder: this.placeholder,
        readonly: this.readonly,
        required: this.required,
        tabindex: this.tabindex,
        type: 'tel',
        styleClasses: this.inputStyleClasses,
      };
    },
  },
  watch: {
    value: function (newVal) {
      this.phoneNumber = newVal;
    },
  },
  methods: {
    input(value, object) {
      this.validatePhoneNumber(object);
      this.$emit('input', value, this.phoneNumberInfo);
    },
    blur() {
      this.$emit('blur', {});
    },
    focus() {
      this.$emit('focus', {});
    },
    open() {
      this.$emit('open', {});
    },
    close() {
      this.$emit('close', {});
    },
    space() {
      this.$emit('space', {});
    },
    enter() {
      this.$emit('enter', {});
    },
    countryChanged(countryObject) {
      this.$emit('country-changed', countryObject);
    },
    validatePhoneNumber(object) {
      if (object && object.number) {
        const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
        const shortInfo = libphonenumber.ShortNumberInfo.getInstance();
        const parsedNumber = phoneUtil.parseAndKeepRawInput(object.number, object.countryCode);
        const isEmergencyNumber = shortInfo.connectsToEmergencyNumber(
          object.nationalNumber,
          object.countryCode
        );
        object.isEmergencyNumber = isEmergencyNumber;
        object.valid =
          (this.allowEmergencyNumber && isEmergencyNumber) ||
          (this.validationType === 'possible'
            ? phoneUtil.isPossibleNumber(parsedNumber)
            : phoneUtil.isValidNumber(parsedNumber));
      }
      this.phoneNumberInfo = object;
    },
    validate(object) {
      this.validatePhoneNumber(object);
      this.$emit('validate', this.phoneNumberInfo);
    },
    clearInputValue() {
      if (this.disabled) return;
      this.phoneNumber = '';
      this.input('', {});
    },
  },
};
</script>

<style lang="scss">
@import 'IpsTelInput';
</style>
